import type { FlexProps } from '@chakra-ui/react';
import {
  Box,
  Button,
  Flex,
  Image,
  Skeleton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import useBoostTokens from '@ui/hooks/useBoostTokens';
import useFarmAprs, { getNextMultiplier } from '@ui/hooks/useFarmAprs';
import { useTranslations } from '@ui/i18n';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import { formatTokenAmount } from '@ui/utils/format';
import { InColorTheme, inTiaSelector } from '@veno-app/chakra';
import React from 'react';

import type { GardenBoostCardProps } from './GardenBoostCard';
import { GardenBoostCard } from './GardenBoostCard';
import GardenBoostMoreDetail from './GardenBoostMoreDetail';
import { LearnMoreDrawer } from './LearnMoreDrawer';

export const GardenWaterBoostCard = (
  props: Omit<GardenBoostCardProps, 'avatarText'>,
) => {
  const sdk = useVenoSDK();
  const { boostMultiplier } = useFarmAprs();
  const nextBoostMultiplier = getNextMultiplier(sdk.chainType, boostMultiplier);

  return (
    <GardenBoostCard
      avatarText={
        boostMultiplier !== undefined ? boostMultiplier.toFixed(2) + 'x' : ''
      }
      {...props}
    >
      <Image
        position="absolute"
        blendMode="multiply"
        mt="16px"
        src="/images/garden-water-level.png"
        alt="loading"
        w="100%"
      />
      <GardenWaterBoostCardBody
        boostMultiplier={boostMultiplier}
        nextBoostMultiplier={nextBoostMultiplier}
      />
      <GardenBoostMoreDetail
        mt={{
          base: '16px',
          desktop: '10px',
        }}
      />
    </GardenBoostCard>
  );
};

const GardenWaterBoostCardBody = ({
  boostMultiplier,
  nextBoostMultiplier,
  ...props
}: FlexProps & {
  boostMultiplier: number | undefined;
  nextBoostMultiplier: number | undefined;
}) => {
  const t = useTranslations();
  const learnMoreDrawerDisclosure = useDisclosure();
  const {
    isLoading: progressLoading,
    remainingIn96,
    progress,
    isMax,
  } = useBoostTokens();

  const boostPercent = progressLoading
    ? undefined
    : isMax
    ? 100
    : (progress ?? 0) * 100;

  return (
    <Flex flexDir="column" flex={1} {...props}>
      <InColorTheme colorTheme="default">
        <LearnMoreDrawer {...learnMoreDrawerDisclosure} />
      </InColorTheme>
      <Text
        textStyle={{
          base: 'bodySmallBold',
          desktop: 'h4Bold',
        }}
      >
        {t('Active watering boost')}
      </Text>
      <Skeleton isLoaded={boostMultiplier !== undefined} mt="4px">
        <Flex justify="space-between">
          <Box
            textStyle={{
              base: 'caption',
              desktop: 'body',
            }}
          >
            {!isMax ? (
              <>
                {t('Next multiplier')}: {nextBoostMultiplier?.toFixed(2)}x
              </>
            ) : (
              <>
                {t('Maximum boost {num} applied', {
                  num: nextBoostMultiplier?.toFixed(2) ?? 0,
                })}
              </>
            )}
          </Box>
          {boostPercent !== undefined && (
            <Box color="garden.highlight" textStyle="captionBold" ml="auto">
              {boostPercent.toFixed(0)}%
            </Box>
          )}
        </Flex>
      </Skeleton>
      <Box
        mt={1.5}
        w="full"
        rounded="6px"
        bg="bg.main"
        shadow="smallCard.active"
        h="11px"
      >
        {boostPercent !== undefined && (
          <Box
            h="full"
            w={`calc(${Math.floor(boostPercent)}%)`}
            maxW="100%"
            rounded="13px"
            bg="linear-gradient(90.38deg, #6666FF -6.34%, #66D3FF 99.6%)"
            _dark={{
              bg: 'linear-gradient(90.38deg, #676EFF -6.34%, #00FFCF 99.6%)',
            }}
          />
        )}
      </Box>
      <Skeleton isLoaded={isMax || remainingIn96 !== undefined} mt={1.5}>
        <Flex
          flexDir={{
            base: 'column',
            desktop: 'row',
          }}
          alignItems={{
            base: 'flex-start',
            desktop: 'center',
          }}
          gap="7px"
        >
          <Text
            flex="1"
            color="text.light"
            textStyle={{
              base: 'caption',
              desktop: 'body',
            }}
          >
            {!isMax && (
              <>
                {t('lock {num} VNO more for 96 months', {
                  num:
                    nextBoostMultiplier !== 1.05
                      ? formatTokenAmount(remainingIn96, 2) || ''
                      : 0.01,
                })}
              </>
            )}
          </Text>
          <Button
            variant="link"
            color="text-link"
            sx={{
              [inTiaSelector]: {
                color: 'brand-tiaTab',
              },
            }}
            onClick={(e) => {
              learnMoreDrawerDisclosure.onOpen();
              e.stopPropagation();
            }}
          >
            {t('Learn more')}
          </Button>
        </Flex>
      </Skeleton>
    </Flex>
  );
};
